/***
*
*   FEEDBACK
*   View user feedback for your application
*
**********/

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Table, Space, Typography, Button, Tag, Row, Col,
  Statistic, Modal, message, Rate, Progress, Empty
} from 'antd';
import {
  SmileOutlined, MehOutlined, FrownOutlined, DeleteOutlined,
  ReloadOutlined, MailOutlined, HeartOutlined
} from '@ant-design/icons';
import { fetchFeedback, fetchFeedbackMetrics, invalidateFeedbackCache } from '../store/slices/feedbackSlice';
import { Animate } from 'components/lib';
import { PermissionGate } from 'components/gate/PermissionGate';
import Axios from 'axios';

const { Title, Text } = Typography;
const { confirm } = Modal;

export function Feedback() {
  const dispatch = useDispatch();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const feedbackState = useSelector((state) => state.feedback);
  const { data, metrics, loading, metricsLoading } = feedbackState;

  useEffect(() => {
    dispatch(fetchFeedback());
    dispatch(fetchFeedbackMetrics());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(invalidateFeedbackCache());
    dispatch(fetchFeedback());
    dispatch(fetchFeedbackMetrics());
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Delete Feedback',
      content: 'Are you sure you want to delete this feedback item?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await Axios.delete('/api/feedback', { data: { id: record.id || record._id } });
          message.success('Feedback deleted');
          handleRefresh();
        } catch {
          message.error('Failed to delete feedback');
        }
      },
    });
  };

  const handleBulkDelete = () => {
    confirm({
      title: 'Delete Selected Feedback',
      content: `Are you sure you want to delete ${selectedRowKeys.length} items?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await Axios.delete('/api/feedback', { data: { id: selectedRowKeys } });
          message.success('Feedback items deleted');
          setSelectedRowKeys([]);
          handleRefresh();
        } catch {
          message.error('Failed to delete feedback');
        }
      },
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'green';
    if (rating >= 3) return 'orange';
    return 'red';
  };

  const columns = [
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (rating) => (
        <Space>
          <Rate disabled defaultValue={rating} count={5} />
          <Tag color={getRatingColor(rating)}>{rating}/5</Tag>
        </Space>
      ),
      sorter: (a, b) => a.rating - b.rating,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      render: (text) => text || <Text type="secondary">No comment</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Date',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.date_created) - new Date(b.date_created),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.email && (
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => window.location.href = `mailto:${record.email}`}
            />
          )}
          <PermissionGate menu="feedback" action="delete">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </PermissionGate>
        </Space>
      ),
    },
  ];

  const tableData = (data || []).map(item => ({
    ...item,
    key: item.id || item._id,
  }));

  const total = (metrics?.positive || 0) + (metrics?.neutral || 0) + (metrics?.negative || 0);
  // const positivePercent = total > 0 ? ((metrics?.positive || 0) / total * 100).toFixed(0) : 0;

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <HeartOutlined style={{ marginRight: 8 }} />
              Feedback
            </Title>
            <Text type="secondary">View user feedback for your application</Text>
          </div>
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button danger onClick={handleBulkDelete}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
          </Space>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card loading={metricsLoading}>
              <Statistic
                title="Positive"
                value={metrics?.positive || 0}
                prefix={<SmileOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
              <Progress percent={total > 0 ? ((metrics?.positive || 0) / total * 100).toFixed(0) : 0} strokeColor="#52c41a" showInfo={false} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card loading={metricsLoading}>
              <Statistic
                title="Neutral"
                value={metrics?.neutral || 0}
                prefix={<MehOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
              <Progress percent={total > 0 ? ((metrics?.neutral || 0) / total * 100).toFixed(0) : 0} strokeColor="#faad14" showInfo={false} />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card loading={metricsLoading}>
              <Statistic
                title="Negative"
                value={metrics?.negative || 0}
                prefix={<FrownOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
              <Progress percent={total > 0 ? ((metrics?.negative || 0) / total * 100).toFixed(0) : 0} strokeColor="#ff4d4f" showInfo={false} />
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Card>
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              pageSize: 25,
              showTotal: (total) => `Total ${total} feedback items`,
            }}
            locale={{ emptyText: <Empty description="No feedback yet" /> }}
          />
        </Card>
      </div>
    </Animate>
  );
}